import { AnalyticsType } from "@src/appV2/Analytics/constants";
import { HOME_HEALTH_CASES_PATH } from "@src/appV2/ExperimentalHomeHealth/Cases/paths";
import { HOME_HEALTH_VISIT_VERIFICATION_PATH } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/paths";
import { getLocation } from "@src/appV2/redesign/utils/routes";

import { type WorkplaceMenuPrivileges } from "../privilegesProviders/WorkplaceMenuPrivilegesProvider";
import { type NavigationItem, type NavigationMenu, type NavigationSection } from "./types";
import { getRouteAndSelected } from "./utils";

interface AnalyticsConfig {
  type: AnalyticsType;
  embedId: string;
}

interface WorkplaceNavigationOptions {
  unreadChannelCount?: number;
  pendingExtraWorkedTimeRequestCount?: number;
}

export function getWorkplaceNavigationMenu(
  workplaceMenuPrivileges: WorkplaceMenuPrivileges,
  currentPath: string,
  analyticsConfig: AnalyticsConfig[] = [],
  options: WorkplaceNavigationOptions = {}
): NavigationMenu {
  const { unreadChannelCount, pendingExtraWorkedTimeRequestCount } = options;
  const items: Array<NavigationItem | NavigationSection> = [];

  // Home Health specific items (shown at top for Home Health workplaces)
  if (workplaceMenuPrivileges.homeHealthSection) {
    if (workplaceMenuPrivileges.homeHealthSection.cases) {
      items.push({
        label: "Cases",
        iconType: "profile",
        routeTo: { pathname: `/v2/home/${HOME_HEALTH_CASES_PATH}` },
        isSelected: currentPath.includes(`/v2/home/${HOME_HEALTH_CASES_PATH}`),
      });
    }
    if (workplaceMenuPrivileges.homeHealthSection.visitVerification) {
      items.push({
        label: "Visit Verification",
        iconType: "badge-check",
        routeTo: { pathname: `/v2/home/${HOME_HEALTH_VISIT_VERIFICATION_PATH}` },
        isSelected: currentPath.includes(`/v2/home/${HOME_HEALTH_VISIT_VERIFICATION_PATH}`),
      });
    }
  }

  if (workplaceMenuPrivileges.workplaceOnboarding) {
    items.push({
      label: "Getting started",
      iconType: "hybrid-view",
      ...getRouteAndSelected("facilityOnboarding", currentPath),
    });
  }

  if (workplaceMenuPrivileges.dailyView) {
    items.push({
      label: "Daily View",
      iconType: "calendar-day",
      ...getRouteAndSelected("dailyView", currentPath),
    });
  }

  if (workplaceMenuPrivileges.calendar) {
    items.push({
      label: "Calendar",
      iconType: "calendar",
      ...getRouteAndSelected("facilityDashboard", currentPath),
    });
  }

  if (workplaceMenuPrivileges.chat) {
    const chatRouteKey = workplaceMenuPrivileges.chatRedesign ? "chat" : "facilityChat";
    items.push({
      label: "Chat",
      iconType: "message",
      ...getRouteAndSelected(chatRouteKey, currentPath),
      badgeCount: unreadChannelCount,
    });
  }

  if (workplaceMenuPrivileges.professionalsSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.professionalsSection.myProfessionals) {
      sectionItems.push({
        label: "My Professionals",
        iconType: "folder-person",
        ...getRouteAndSelected("workplaceWorkers", currentPath),
      });
    }
    if (workplaceMenuPrivileges.professionalsSection.browseProfessionals) {
      sectionItems.push({
        label: "Browse Professionals",
        iconType: "people",
        ...getRouteAndSelected("workersDirectory", currentPath),
      });
    }

    items.push({ label: "Professionals", items: sectionItems });
  }

  if (workplaceMenuPrivileges.permanentHiringSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.permanentHiringSection.placementsLandingPage) {
      sectionItems.push({
        label: "Landing Page",
        iconType: "ribbon",
        ...getRouteAndSelected("jobsLandingPage", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewSchedule) {
      sectionItems.push({
        label: "Interview Schedule",
        iconType: "clock-outlined",
        ...getRouteAndSelected("interviewSchedule", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.applicationAndJobs) {
      sectionItems.push({
        label: "Application & Jobs",
        iconType: "briefcase",
        ...getRouteAndSelected("manageJobs", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.browseTalentPool) {
      sectionItems.push({
        label: "Browse Talent Pool",
        iconType: "person-superstar",
        ...getRouteAndSelected("workplaceHireProfessionals", currentPath),
      });
    }
    if (workplaceMenuPrivileges.permanentHiringSection.interviewAvailability) {
      sectionItems.push({
        label: "Interview Availability",
        iconType: "person-male-formal",
        ...getRouteAndSelected("interviewSettings", currentPath),
      });
    }

    items.push({ label: "Permanent Hiring", items: sectionItems });
  }

  if (workplaceMenuPrivileges.charting) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Charting",
      iconType: "chart-line-increase",
      ...getRouteAndSelected("workplaceCharting", currentPath),
    });

    items.push({ label: "Charting", items: sectionItems });
  }

  if (workplaceMenuPrivileges.analyticsSection) {
    const sectionItems: NavigationItem[] = [];
    const workplaceAnalyticsPath = "/workplace/analytics";

    const workplaceAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.WORKPLACE_FLEX
    )?.embedId;

    const corporateAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.CORPORATE_FLEX
    )?.embedId;

    if (workplaceMenuPrivileges.analyticsSection.workplaceAnalytics && workplaceAnalyticsEmbedId) {
      sectionItems.push({
        label: "Workplace Analytics",
        iconType: "microscope",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: workplaceAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    // Only show corporate analytics in Analytics section when Corporate Center is not visible
    if (
      !workplaceMenuPrivileges.corporateCenterSection &&
      workplaceMenuPrivileges.analyticsSection.corporateAnalytics &&
      corporateAnalyticsEmbedId
    ) {
      sectionItems.push({
        label: "Corporate Analytics",
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: corporateAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: "Analytics", items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.corporateCenterSection) {
    const sectionItems: NavigationItem[] = [];
    const workplaceAnalyticsPath = "/workplace/analytics";
    const spendingControlsPath = "/corporate/spending-controls";

    const corporateAnalyticsEmbedId = analyticsConfig.find(
      (config) => config.type === AnalyticsType.CORPORATE_FLEX
    )?.embedId;

    if (
      workplaceMenuPrivileges.corporateCenterSection.corporateAnalytics &&
      corporateAnalyticsEmbedId
    ) {
      sectionItems.push({
        label: "Corporate Analytics",
        iconType: "binoculars",
        routeTo: getLocation("workplaceAnalytics", {
          queryParams: { embedId: corporateAnalyticsEmbedId },
        }),
        isSelected: currentPath.includes(workplaceAnalyticsPath),
      });
    }

    if (workplaceMenuPrivileges.corporateCenterSection.spendingControls) {
      sectionItems.push({
        label: "Spend Controls",
        iconType: "gauge",
        routeTo: getLocation("corporateSpendingControls"),
        isSelected: currentPath.includes(spendingControlsPath),
      });
    }

    if (sectionItems.length > 0) {
      items.push({ label: "Corporate Center", items: sectionItems });
    }
  }

  if (workplaceMenuPrivileges.liveWorkerETA) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Live Worker ETA",
      iconType: "coordinates-person",
      ...getRouteAndSelected("facilityLiveWorkerETA", currentPath),
    });

    items.push({ label: "Live Worker ETA", items: sectionItems });
  }

  if (workplaceMenuPrivileges.workplaceRulesQuiz) {
    items.push({
      label: "Workplace Rules Quiz",
      iconType: "book",
      routeTo: getLocation("facilityWorkerInstructions", {
        queryParams: { click_src: "menu_item" },
      }),
      isSelected: currentPath.includes("/facility/worker-instructions"),
    });
  }

  if (workplaceMenuPrivileges.managementSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.managementSection.myAccount) {
      sectionItems.push({
        label: "My Account",
        iconType: "person-male",
        ...getRouteAndSelected("myAccount", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.teamMembers) {
      sectionItems.push({
        label: "Team Members",
        iconType: "group",
        ...getRouteAndSelected("workplaceUsers", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.workplaceInformation) {
      sectionItems.push({
        label: "Workplace Information",
        iconType: "information",
        ...getRouteAndSelected("facilityWorkerInstructions", currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.settings) {
      const settingsRouteKey = workplaceMenuPrivileges.settingsRedesign
        ? "settings"
        : "facilitySettings";
      sectionItems.push({
        label: "Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected(settingsRouteKey, currentPath),
      });
    }

    if (workplaceMenuPrivileges.managementSection.timeclockingSettings) {
      sectionItems.push({
        label: "Timeclocking Settings",
        iconType: "cogwheel",
        ...getRouteAndSelected("facilityTimeclockSettings", currentPath),
      });
    }

    items.push({ label: "Management", items: sectionItems });
  }

  if (workplaceMenuPrivileges.billingSection) {
    const sectionItems: NavigationItem[] = [];

    if (workplaceMenuPrivileges.billingSection.upcomingCharges) {
      sectionItems.push({
        label: "Upcoming Charges",
        iconType: "dollar",
        ...getRouteAndSelected("facilityInvoiceUpcomingCharges", currentPath),
        badgeCount: pendingExtraWorkedTimeRequestCount,
      });
    }

    if (workplaceMenuPrivileges.billingSection.workplaceSpendingControls) {
      sectionItems.push({
        label: "Spend Controls",
        iconType: "gauge",
        ...getRouteAndSelected("workplaceSpendingControls", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.invoiceHistory) {
      sectionItems.push({
        label: "Invoice History",
        iconType: "money-dollar",
        ...getRouteAndSelected("facilityInvoiceHistory", currentPath),
      });
    }

    if (workplaceMenuPrivileges.billingSection.contractTerms) {
      sectionItems.push({
        label: "Contract Terms",
        iconType: "notepad-alt",
        ...getRouteAndSelected("facilityInvoiceContractTerms", currentPath),
      });
    }

    items.push({ label: "Billing", items: sectionItems });
  }

  if (workplaceMenuPrivileges.helpCenter) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Help Center",
      iconType: "question-circle",
      ...getRouteAndSelected("helpCenter", currentPath),
    });

    items.push({ label: "Help Center", items: sectionItems });
  }

  if (workplaceMenuPrivileges.referAndEarn) {
    const sectionItems: NavigationItem[] = [];

    sectionItems.push({
      label: "Refer & Earn",
      iconType: "palm-dollar",
      ...getRouteAndSelected("workplaceReferAndEarn", currentPath),
    });

    items.push({ label: "Refer & Earn", items: sectionItems });
  }

  return { items };
}
